import { useAuth } from '../../contexts/AuthContext';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { AlertCircle, Calendar, Download, FileSpreadsheet, FileText, Loader2, Printer } from 'lucide-react';
import { downloadAuthenticatedFile } from '../../services/reportsApi';
import { buildApiUrl } from '../../utils/apiSetup';

const MONTHS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const currentYr = new Date().getFullYear();
const YEARS = Array.from({ length: Math.max(3, currentYr - 2024 + 2) }, (_, i) => String(2025 + i));

const CpfMissingPranReportView = ({
  initialYear = String(new Date().getFullYear()),
  initialMonth = String(new Date().getMonth() + 1),
  initialDepartment = 'ALL',
  departmentsList = [],
}) => {
  const { hasPermission } = useAuth();
  const [year, setYear] = useState(initialYear);
  const [month, setMonth] = useState(initialMonth);
  const [department, setDepartment] = useState(initialDepartment);
  const [ddoCode, setDdoCode] = useState('CGV015024C');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [error, setError] = useState('');
  const printRef = useRef(null);

  const departmentOptions = useMemo(() => {
    const names = departmentsList.map((dept) => dept.name).filter(Boolean);
    return ['ALL', ...Array.from(new Set(names)).sort()];
  }, [departmentsList]);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams({ year, month });
        if (department && department !== 'ALL') {
          params.set('department', department);
        }
        if (ddoCode && ddoCode.trim()) {
          params.set('ddo_code', ddoCode.trim());
        }
        const response = await fetch(buildApiUrl(`/api/v1/reports/cpf-missing-pran-absent?${params}`), {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('Failed to load Missing PRAN & Absent Report');
        const json = await response.json();
        setData(json);
        if (json.ddo_reg_no && !ddoCode) {
          setDdoCode(json.ddo_reg_no);
        }
      } catch (err) {
        if (err.name !== 'AbortError') setError(err.message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    if (/^\d{4}$/.test(year) && month) load();
    return () => controller.abort();
  }, [year, month, department, ddoCode]);

  const downloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const params = new URLSearchParams({ year, month });
      if (department && department !== 'ALL') {
        params.set('department', department);
      }
      if (ddoCode && ddoCode.trim()) {
        params.set('ddo_code', ddoCode.trim());
      }
      const monthLabel = MONTHS.find((item) => item.value === month)?.label || month;
      await downloadAuthenticatedFile(
        `/api/v1/reports/cpf-missing-pran-absent-excel?${params}`,
        `CPF_Without_PRAN_and_Absent_${monthLabel}_${year}.xlsx`,
      );
    } catch (err) {
      setError(err.message || 'Failed to download Excel report');
    } finally {
      setDownloadingExcel(false);
    }
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const params = new URLSearchParams({ year, month });
      if (department && department !== 'ALL') {
        params.set('department', department);
      }
      if (ddoCode && ddoCode.trim()) {
        params.set('ddo_code', ddoCode.trim());
      }
      const monthLabel = MONTHS.find((item) => item.value === month)?.label || month;
      await downloadAuthenticatedFile(
        `/api/v1/reports/cpf-missing-pran-absent-pdf?${params}`,
        `CPF_Without_PRAN_and_Absent_${monthLabel}_${year}.pdf`,
      );
    } catch (err) {
      setError(err.message || 'Failed to download PDF report');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const monthLabel = MONTHS.find((m) => m.value === month)?.label?.toUpperCase() || month;
  const reportHeading = data?.report_title || `EMPLOYEES WITHOUT PRAN NOS. AND ABSENT FROM DUTIES FOR THE MONTH OF ${monthLabel} ${year}`;

  return (
    <div className="space-y-5">
      {/* Printable styles */}
      <style>{`
        @media print {
          @page {
            size: landscape;
            margin: 8mm;
          }
          body {
            background: white !important;
            color: black !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .printable-report {
            border: 1px solid black !important;
            box-shadow: none !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          table {
            font-size: 8pt !important;
          }
          th, td {
            border: 1px solid black !important;
            padding: 3px 4px !important;
          }
        }
      `}</style>

      {/* Control bar */}
      <div className="print:hidden flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <Calendar className="mb-2 h-5 w-5 text-rose-600" />
        <label className="text-xs font-semibold text-gray-500">
          YEAR
          <select
            value={year}
            onChange={(event) => setYear(event.target.value)}
            className="mt-1 block w-24 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-bold text-gray-800"
          >
            {YEARS.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
        </label>
        <label className="text-xs font-semibold text-gray-500">
          MONTH
          <select
            value={month}
            onChange={(event) => setMonth(event.target.value)}
            className="mt-1 block w-32 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-bold text-gray-800"
          >
            {MONTHS.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </label>
        <label className="text-xs font-semibold text-gray-500">
          DEPARTMENT
          <select
            value={department}
            onChange={(event) => setDepartment(event.target.value)}
            className="mt-1 block min-w-[160px] rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-bold text-gray-800"
          >
            {departmentOptions.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
        </label>
        <label className="text-xs font-semibold text-gray-500">
          DDO REG. NO.
          <input
            type="text"
            value={ddoCode}
            onChange={(event) => setDdoCode(event.target.value.toUpperCase())}
            placeholder="DDO Reg. Number"
            className="mt-1 block w-36 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-bold text-gray-800 uppercase"
          />
        </label>

        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={handlePrint}
            disabled={loading}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3.5 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            title="Print report in landscape"
          >
            <Printer className="h-4 w-4 text-gray-600" />
            Print
          </button>
          {hasPermission('report:export') && (
            <>
              <button
                onClick={downloadPdf}
                disabled={downloadingPdf || loading}
                className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3.5 py-1.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {downloadingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                PDF
              </button>
              <button
                onClick={downloadExcel}
                disabled={downloadingExcel || loading}
                className="flex items-center gap-1.5 rounded-lg bg-green-600 px-3.5 py-1.5 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
              >
                {downloadingExcel ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                Excel
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {/* Report Container */}
      <div
        ref={printRef}
        className="printable-report overflow-x-auto rounded-xl border-2 border-gray-800 bg-white p-5 shadow-lg"
      >
        {/* Report Header matching Reference Image 2 */}
        <div className="mb-4 border border-gray-800 p-2.5 text-center bg-gray-50/50">
          <h2 className="text-sm md:text-base font-bold uppercase tracking-wide text-gray-900">
            {reportHeading}
          </h2>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-rose-600" />
          </div>
        ) : (
          <>
            <table className="w-full min-w-[900px] border-collapse font-sans text-xs text-black border border-gray-800">
              <thead>
                <tr className="bg-gray-100 font-bold border-b border-gray-800">
                  <th className="border border-gray-800 px-2 py-2 text-center w-12">Sr. No.</th>
                  <th className="border border-gray-800 px-2 py-2 text-center w-20">CODE NO.</th>
                  <th className="border border-gray-800 px-2 py-2 text-center w-28">DDO REG. NO.</th>
                  <th className="border border-gray-800 px-3 py-2 text-left">NAME OF EMPLOYEE</th>
                  <th className="border border-gray-800 px-3 py-2 text-right w-28">
                    {data?.employer_percentage != null ? `${data.employer_percentage}%` : '14%'} Govt. Contribution
                  </th>
                  <th className="border border-gray-800 px-3 py-2 text-right w-28">
                    {data?.employee_percentage != null ? `${data.employee_percentage}%` : '10%'} Subscriber Contribution
                  </th>
                  <th className="border border-gray-800 px-2 py-2 text-center w-20">MONTH</th>
                  <th className="border border-gray-800 px-2 py-2 text-center w-16">YEAR</th>
                  <th className="border border-gray-800 px-2 py-2 text-center w-20">TYPE</th>
                  <th className="border border-gray-800 px-3 py-2 text-left w-64">PRAN NO. /REMARK</th>
                </tr>
              </thead>
              <tbody>
                {(data?.rows || []).map((row) => (
                  <tr key={`${row.code_no}-${row.sr_no}`} className="hover:bg-gray-50/80">
                    <td className="border border-gray-800 px-2 py-1 text-center font-mono">{row.sr_no}</td>
                    <td className="border border-gray-800 px-2 py-1 text-center font-mono">{row.code_no}</td>
                    <td className="border border-gray-800 px-2 py-1 text-center font-mono">{row.ddo_reg_no}</td>
                    <td className="border border-gray-800 px-3 py-1 font-medium uppercase">{row.name_of_employee}</td>
                    <td className="border border-gray-800 px-3 py-1 text-right font-mono">
                      {row.govt_contribution.toLocaleString('en-IN')}
                    </td>
                    <td className="border border-gray-800 px-3 py-1 text-right font-mono">
                      {row.subscriber_contribution.toLocaleString('en-IN')}
                    </td>
                    <td className="border border-gray-800 px-2 py-1 text-center uppercase">{row.month}</td>
                    <td className="border border-gray-800 px-2 py-1 text-center font-mono">{row.year}</td>
                    <td className="border border-gray-800 px-2 py-1 text-center">{row.type}</td>
                    <td className="border border-gray-800 px-3 py-1 font-medium text-gray-800">
                      {row.pran_remark}
                    </td>
                  </tr>
                ))}

                {/* Totals Row matching Image 2 */}
                {!loading && (data?.rows || []).length > 0 && (
                  <tr className="bg-gray-100 font-bold text-xs border-t-2 border-gray-900">
                    <td colSpan={4} className="border border-gray-800 px-3 py-2 text-center uppercase tracking-wider">
                      TOTAL
                    </td>
                    <td className="border border-gray-800 px-3 py-2 text-right font-mono">
                      {(data?.total_govt_contribution || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="border border-gray-800 px-3 py-2 text-right font-mono">
                      {(data?.total_subscriber_contribution || 0).toLocaleString('en-IN')}
                    </td>
                    <td colSpan={4} className="border border-gray-800 px-2 py-2 bg-gray-100"></td>
                  </tr>
                )}

                {/* Empty State */}
                {!loading && (!data?.rows || data.rows.length === 0) && (
                  <tr>
                    <td colSpan={10} className="border border-gray-800 px-3 py-10 text-center text-gray-500">
                      No exceptional records (missing PRAN, retired, or absent) found for the selected period.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Bottom Signatures matching Image 2 */}
            <div className="mt-12 grid grid-cols-2 text-xs font-bold text-gray-900 px-4">
              <div>
                <p className="uppercase">{data?.signatories?.left_title || 'PROVIDENT FUND CLERK'}</p>
                <p className="uppercase text-gray-700">{data?.signatories?.left_org || 'PUNE CANTONMENT BOARD'}</p>
              </div>
              <div className="text-right">
                <p className="uppercase">{data?.signatories?.right_title || 'CHIEF ACCOUNTANT'}</p>
                <p className="uppercase text-gray-700">{data?.signatories?.right_org || 'PUNE CANTONMENT BOARD'}</p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CpfMissingPranReportView;
